import type { ManifestEntry, SubseccaoDoManifesto, TopicoDoManifesto } from '../content/load.ts';
import type { LessonState } from './lessonProgress.ts';

/**
 * ADR 0027 — o progresso de um curso, derivado por níveis: tópico (a lição),
 * subsecção, secção (o módulo) e curso (a trilha).
 *
 * A fonte única continua a ser a lição — concluída, ou com `index > 0` quando
 * está a meio — e tudo aqui é contagem sobre o manifesto. **Nenhuma
 * percentagem se guarda**, pela mesma razão de `percursos.ts`: guardar a
 * origem e derivar o total é o que permite recalcular quando o conteúdo
 * cresce ou a arrumação em subsecções muda.
 *
 * Tudo se calcula só com o manifesto. É deliberado: a página do curso é a
 * segunda pergunta do estudante («onde é que eu ia») e tem de abrir depressa
 * numa ligação má (RNF-002) — descarregar os pacotes de conteúdo para contar
 * tópicos seria pagar meio megabyte por uma lista.
 */

export type EstadoDeProgresso = 'concluida' | 'em-curso' | 'por-comecar';

export interface Contagem {
  feitas: number;
  total: number;
  estado: EstadoDeProgresso;
}

export type Progresso = Readonly<Record<string, LessonState | undefined>>;

function estadoDe(feitas: number, total: number, algoAMeio: boolean): EstadoDeProgresso {
  if (total > 0 && feitas === total) return 'concluida';
  return feitas > 0 || algoAMeio ? 'em-curso' : 'por-comecar';
}

/** Quantos exercícios de uma lição já foram feitos — o índice avança ao resolver. */
function exerciciosFeitos(id: string, progresso: Progresso, total: number): number {
  return Math.min(progresso[id]?.index ?? 0, total);
}

/** O tópico: exercícios feitos sobre exercícios. */
export function progressoDoTopico(
  topico: TopicoDoManifesto,
  concluidas: ReadonlySet<string>,
  progresso: Progresso,
): Contagem {
  if (concluidas.has(topico.id)) {
    return { feitas: topico.exercicios, total: topico.exercicios, estado: 'concluida' };
  }
  const feitas = exerciciosFeitos(topico.id, progresso, topico.exercicios);
  // Abrir uma lição e sair sem resolver nada não é progresso: `index === 0`.
  return { feitas, total: topico.exercicios, estado: feitas > 0 ? 'em-curso' : 'por-comecar' };
}

/** A subsecção: tópicos concluídos sobre tópicos. */
export function progressoDaSubseccao(
  subseccao: SubseccaoDoManifesto,
  concluidas: ReadonlySet<string>,
  progresso: Progresso,
): Contagem {
  const feitas = subseccao.topicos.filter((t) => concluidas.has(t.id)).length;
  const aMeio = subseccao.topicos.some(
    (t) => !concluidas.has(t.id) && exerciciosFeitos(t.id, progresso, t.exercicios) > 0,
  );
  return { feitas, total: subseccao.topicos.length, estado: estadoDe(feitas, subseccao.topicos.length, aMeio) };
}

/** A secção (o módulo): subsecções concluídas sobre subsecções. */
export function progressoDaSeccao(
  modulo: ManifestEntry,
  concluidas: ReadonlySet<string>,
  progresso: Progresso,
): Contagem {
  const das = modulo.subseccoes.map((s) => progressoDaSubseccao(s, concluidas, progresso));
  const feitas = das.filter((c) => c.estado === 'concluida').length;
  const aMeio = das.some((c) => c.estado === 'em-curso');
  return { feitas, total: das.length, estado: estadoDe(feitas, das.length, aMeio) };
}

/**
 * O curso (a trilha): secções concluídas sobre secções — a contagem que o
 * cartão mostra — e a percentagem, que se mede em **lições** e não em
 * secções: é a medida que o certificado usa (RN-003, 100% das lições) e a
 * que mexe enquanto se está a meio de uma secção. As duas coisas
 * apresentam-se juntas («21% · 3/14») e são deliberadamente medidas em
 * unidades diferentes.
 */
export function progressoDoCurso(
  modulos: ManifestEntry[],
  concluidas: ReadonlySet<string>,
  progresso: Progresso,
): Contagem & { percentagem: number } {
  const seccoes = modulos.map((m) => progressoDaSeccao(m, concluidas, progresso));
  const feitas = seccoes.filter((c) => c.estado === 'concluida').length;
  const aMeio = seccoes.some((c) => c.estado === 'em-curso');
  const licoes = modulos.flatMap((m) => m.lessons);
  const licoesFeitas = licoes.filter((id) => concluidas.has(id)).length;
  return {
    feitas,
    total: seccoes.length,
    estado: estadoDe(feitas, seccoes.length, aMeio),
    percentagem: licoes.length === 0 ? 0 : Math.floor((licoesFeitas / licoes.length) * 100),
  };
}

/** Os módulos de uma trilha, pela ordem declarada. */
export function seccoesDoCurso(modulos: ManifestEntry[], trilha: string): ManifestEntry[] {
  return modulos.filter((m) => m.track === trilha).sort((a, b) => a.order_index - b.order_index);
}

/**
 * As lições da trilha, pela ordem em que se estudam. É a mesma ordem de que
 * o compilador deriva os pré-requisitos (cada lição depende da anterior, a
 * primeira de cada trilha de nada), e é por isso que chega para dizer se um
 * tópico está bloqueado sem descarregar pacote nenhum.
 */
export function ordemDoCurso(seccoes: ManifestEntry[]): string[] {
  return seccoes.flatMap((m) => m.lessons);
}

export type SituacaoDoTopico =
  | { estado: 'concluida' | 'disponivel' }
  | { estado: 'bloqueada'; falta: string };

/**
 * RF-014 — o tópico abre quando o anterior está concluído. `desbloqueado`
 * salta a sequência (demonstração ou conta de acesso total — decidido em
 * `state/desbloqueio.ts`, e não é pagamento).
 */
export function situacaoDoTopico(
  id: string,
  ordem: readonly string[],
  concluidas: ReadonlySet<string>,
  desbloqueado = false,
): SituacaoDoTopico {
  if (concluidas.has(id)) return { estado: 'concluida' };
  if (desbloqueado) return { estado: 'disponivel' };
  const posicao = ordem.indexOf(id);
  const anterior = posicao > 0 ? ordem[posicao - 1] : undefined;
  if (anterior && !concluidas.has(anterior)) return { estado: 'bloqueada', falta: anterior };
  return { estado: 'disponivel' };
}

/**
 * A secção onde o estudante vai: a primeira por concluir. Quando estão todas
 * concluídas, a última — o curso acabou e é lá que se diz.
 */
export function seccaoActual(
  seccoes: ManifestEntry[],
  concluidas: ReadonlySet<string>,
  progresso: Progresso,
): ManifestEntry | undefined {
  return (
    seccoes.find((m) => progressoDaSeccao(m, concluidas, progresso).estado !== 'concluida') ??
    seccoes[seccoes.length - 1]
  );
}

/**
 * O tópico onde se continua (H-220): a lição **a meio** — começada e não
 * concluída — se houver uma, senão a primeira por concluir, pela ordem da
 * trilha. Com o desbloqueio sequencial as duas coincidem; com acesso total
 * ou demonstração, quem saltou lições volta à que deixou a meio e não à
 * primeira que nunca abriu.
 */
export function proximoTopico(
  seccoes: ManifestEntry[],
  concluidas: ReadonlySet<string>,
  progresso: Progresso = {},
): string | undefined {
  const ordem = ordemDoCurso(seccoes);
  return (
    ordem.find((id) => !concluidas.has(id) && (progresso[id]?.index ?? 0) > 0) ??
    ordem.find((id) => !concluidas.has(id))
  );
}

/** O título de um tópico, procurado no manifesto. Devolve o id se não existir. */
export function tituloDoTopico(seccoes: ManifestEntry[], id: string): string {
  for (const m of seccoes) {
    for (const s of m.subseccoes) {
      const t = s.topicos.find((x) => x.id === id);
      if (t) return t.title;
    }
  }
  return id;
}
